import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Phone, Mail, MapPin } from "lucide-react";
import { businessInfo, navLinks } from "../mock";
import { damageServices, cities, cityUrl, damageUrl } from "../data/seoContent";
import { carriers } from "../data/carriers";
import { underpaidClaims } from "../data/underpaidClaims";

const LOGO_URL = "/assets/claim-beast-logo.png";

const Footer = () => {
  const damageList = Object.values(damageServices);
  const cityList = Object.values(cities);

  return (
    <footer className="bg-[#E11D26] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <div className="bg-black p-5 inline-block border border-black">
            <img
              src={LOGO_URL}
              alt="Claim Beast Public Adjusters logo"
              className="h-24 w-auto"
            />
          </div>
          <p className="mt-5 text-white/95 text-sm leading-relaxed">
            Florida &amp; Georgia licensed public adjusters fighting for Tampa
            Bay homeowners. We don&apos;t get paid until you do.
          </p>
          <div className="mt-5 flex gap-3">
            <a
              href={businessInfo.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 w-9 rounded-full bg-black/30 hover:bg-black hover:text-white flex items-center justify-center transition-colors"
              aria-label="Claim Beast on Facebook"
              data-testid="footer-facebook-link"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href={businessInfo.social.googleBusiness}
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 w-9 rounded-full bg-black/30 hover:bg-black hover:text-white flex items-center justify-center transition-colors"
              aria-label="Claim Beast on Google Business Profile"
              data-testid="footer-google-business-link"
            >
              {/* Google "G" mark — inline SVG so we don't add a new dep */}
              <svg
                viewBox="0 0 48 48"
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fill="#FFFFFF"
                  d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
                />
              </svg>
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4
            className="text-sm font-black tracking-[0.25em] uppercase border-b border-white/30 pb-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Damage Types
          </h4>
          <ul className="mt-4 grid grid-cols-1 gap-1.5 text-sm">
            {damageList.map((d) => (
              <li key={d.slug}>
                <Link to={damageUrl(d.slug)} className="hover:underline">
                  {d.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4
            className="text-sm font-black tracking-[0.25em] uppercase border-b border-white/30 pb-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Service Areas
          </h4>
          <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
            {cityList.map((c) => (
              <li key={c.slug}>
                <Link to={cityUrl(c.slug)} className="hover:underline">
                  {c.name}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/public-adjuster-pensacola" className="hover:underline" data-testid="footer-link-pensacola">
                Pensacola
              </Link>
            </li>
            <li>
              <Link to="/public-adjuster-destin" className="hover:underline" data-testid="footer-link-destin">
                Destin
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4
            className="text-sm font-black tracking-[0.25em] uppercase border-b border-white/30 pb-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Contact
          </h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Riverview, FL
                <br />
                Tampa Bay Area
              </span>
            </li>
            <li>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="flex items-center gap-2 hover:underline"
              >
                <Phone className="h-4 w-4" />
                {businessInfo.phone}
              </a>
            </li>
            <li>
              <a
                href={`mailto:${businessInfo.emailJoe}`}
                className="flex items-center gap-2 hover:underline break-all"
              >
                <Mail className="h-4 w-4 shrink-0" />
                {businessInfo.emailJoe}
              </a>
            </li>
          </ul>

          <h4
            className="mt-6 text-sm font-black tracking-[0.25em] uppercase border-b border-white/30 pb-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Quick Links
          </h4>
          <ul className="mt-4 space-y-1.5 text-sm">
            {navLinks.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="hover:underline">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/20 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <span
              className="text-xs font-black tracking-[0.25em] uppercase text-white shrink-0"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Florida Claim Help
            </span>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/denied-insurance-claim-florida" className="hover:underline">
                Denied Insurance Claim Florida
              </Link>
              <Link to="/underpaid-insurance-claim-florida" className="hover:underline">
                Underpaid Insurance Claim Florida
              </Link>
              <Link to="/roof-claim-denial-florida" className="hover:underline">
                Roof Claim Denial Florida
              </Link>
              <Link to="/biohazard-bodily-fluid-claims-florida" className="hover:underline">
                Biohazard &amp; Bodily Fluid Cleanup Claims
              </Link>
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <span
              className="text-xs font-black tracking-[0.25em] uppercase text-white shrink-0"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Guides
            </span>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/guides" className="hover:underline font-bold">
                All Guides
              </Link>
              <Link to="/public-adjuster-vs-insurance-company-adjuster" className="hover:underline">
                Public Adjuster vs. Insurance Adjuster
              </Link>
              <Link to="/do-i-need-a-public-adjuster-in-florida" className="hover:underline">
                Do I Need a Public Adjuster?
              </Link>
              <Link to="/public-adjuster-vs-attorney" className="hover:underline">
                Public Adjuster vs. Attorney
              </Link>
              <Link to="/how-much-does-a-public-adjuster-cost-in-florida" className="hover:underline">
                Public Adjuster Cost
              </Link>
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
            <span
              className="text-xs font-black tracking-[0.25em] uppercase text-white shrink-0"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Underpaid Claims
            </span>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/underpaid-insurance-claim-florida" className="hover:underline font-bold">
                All Underpaid Claims
              </Link>
              {Object.values(underpaidClaims).map((u) => (
                <Link key={u.slug} to={`/${u.slug}`} className="hover:underline">
                  {u.peril}
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
            <span
              className="text-xs font-black tracking-[0.25em] uppercase text-white shrink-0"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              By Carrier
            </span>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              {Object.values(carriers).map((c) => (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  className={`hover:underline ${c.isPillar ? "font-bold" : ""}`}
                >
                  {c.isPillar ? "All Carriers" : c.topic}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/85">
          <div className="flex flex-wrap gap-x-5 gap-y-1 justify-center md:justify-start">
            <span>Firm License {businessInfo.licenses.firm}</span>
            <span>Florida License {businessInfo.licenses.florida}</span>
            <span>Georgia License {businessInfo.licenses.georgia}</span>
          </div>
          <div>
            © {new Date().getFullYear()} Claim Beast Public Adjusters, LLC
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
